import React, { useCallback, useState } from 'react';
import { Frame, Toast, Loading, Text, InlineStack, Popover, ActionList, Avatar, UnstyledButton } from '@shopify/polaris';
import { ExitIcon } from '@shopify/polaris-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { removeToast } from '../../ducks/toast';
import { logout } from '../../ducks/auth';
import { adminLogout } from '../../ducks/admin';
import logo from '../../img/wiser_review_new_logo.svg';

const AFF_MENU = [
  { id: 'tab-dashboard', content: 'Dashboard', path: '/dashboard' },
  { id: 'tab-referrals', content: 'Referrals', path: '/referrals' },
  { id: 'tab-commissions', content: 'Commissions', path: '/commissions' },
  { id: 'tab-payouts', content: 'Payouts', path: '/payouts' },
  { id: 'tab-brand-kit', content: 'Brand kit', path: '/brand-kit' },
  { id: 'tab-settings', content: 'Settings', path: '/settings' }
];

const ADMIN_MENU = [
  { id: 'tab-admin-affiliates', content: 'Affiliates', path: '/admin/affiliates' },
  { id: 'tab-admin-withdrawals', content: 'Withdrawals', path: '/admin/withdrawals' },
  { id: 'tab-admin-commissions', content: 'Commissions', path: '/admin/commissions' }
];

const isOn = (pathname, path) => pathname === path || pathname.startsWith(path + '/');

//which part of the app is shown: public (auth pages), aff (affiliate portal) or admin
export const areaOf = (pathname) => {
  if (pathname.startsWith('/admin') && pathname !== '/admin/login') return 'admin';
  if (AFF_MENU.some((m) => isOn(pathname, m.path))) return 'aff';
  return 'public';
};

//light header: brand + user menu, and the page tabs below it
const AppHeader = ({ area, menu, name, detail, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenu, setUserMenu] = useState(false);
  const selected = Math.max(0, menu.findIndex((m) => isOn(location.pathname, m.path)));

  return (
    <header className={area === 'admin' ? 'aff-header aff-header-wide' : 'aff-header'}>
      <div className="aff-header-inner">
        <div className="aff-header-top">
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <img src={logo} alt="WiserReview" className="aff-logo" />
            <span className="aff-header-badge">{area === 'admin' ? 'Affiliate admin' : 'Affiliate program'}</span>
          </InlineStack>
          <Popover
            active={userMenu}
            onClose={() => setUserMenu(false)}
            preferredAlignment="right"
            activator={
              <UnstyledButton className="aff-user-btn" onClick={() => setUserMenu((v) => !v)} accessibilityLabel="Account menu">
                <InlineStack gap="200" blockAlign="center" wrap={false}>
                  <span className="aff-user-text">
                    <Text as="span" variant="bodyMd" fontWeight="semibold">{name}</Text>
                    {detail && <Text as="span" variant="bodySm" tone="subdued">{detail}</Text>}
                  </span>
                  <Avatar size="md" initials={(name || 'A').trim().charAt(0).toUpperCase()} name={name} />
                </InlineStack>
              </UnstyledButton>
            }
          >
            <ActionList
              actionRole="menuitem"
              items={[{ content: 'Log out', icon: ExitIcon, onAction: () => { setUserMenu(false); onLogout(); } }]}
            />
          </Popover>
        </div>
        {/* plain tab bar (scrolls sideways on small screens) */}
        <nav className="aff-tabs" aria-label="Pages">
          {menu.map((m, i) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={i === selected}
              className={i === selected ? 'aff-tab aff-tab-active' : 'aff-tab'}
              onClick={() => navigate(m.path)}
            >
              {m.content}
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
};

const AppFrame = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const area = areaOf(location.pathname);

  const toasts = useSelector((s) => s.toast.list);
  const isLoading = useSelector((s) => s.loading.count > 0);
  const profile = useSelector((s) => s.auth.profile);

  const handleLogout = useCallback(() => {
    if (area === 'admin') {
      dispatch(adminLogout());
      navigate('/admin/login');
    } else {
      dispatch(logout());
      navigate('/login');
    }
  }, [area, dispatch, navigate]);

  const toastMarkup = toasts.map((t) => (
    <Toast key={t.id} content={t.msg} error={t.error} duration={t.error ? 6000 : 4000} onDismiss={() => dispatch(removeToast(t.id))} />
  ));

  if (area === 'public') {
    return (
      <Frame>
        {isLoading && <Loading />}
        {children}
        {toastMarkup}
      </Frame>
    );
  }

  const menu = area === 'admin' ? ADMIN_MENU : AFF_MENU;
  const name = area === 'admin' ? 'Admin' : (profile ? [profile.fn, profile.ln].filter(Boolean).join(' ') || profile.e : '');
  const detail = area === 'admin' ? '' : (profile ? profile.e : '');

  //no Polaris TopBar / Navigation: no dark bar and no sidebar, the page tabs live in the header
  return (
    <Frame>
      {isLoading && <Loading />}
      <AppHeader area={area} menu={menu} name={name || ' '} detail={detail} onLogout={handleLogout} />
      {children}
      {toastMarkup}
    </Frame>
  );
};

export default AppFrame;
